// Voice input engine: own offline recognition (Vosk WebAssembly, model cached in the app) for German/English,
// otherwise the device speech recognition. Used by the moisture grid and the pressure field.
import { kritisOn } from './policy';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/speech/model`;
export const OFFLINE_LANGS = ['de', 'en'];
const LOC = { de: 'de-DE', en: 'en-US', it: 'it-IT', es: 'es-ES', pt: 'pt-PT', fr: 'fr-FR', sq: 'sq-AL' };
const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
export const appLang = () => localStorage.getItem('baustelle_app_lang') || 'de';
const models = {};
const CACHE = 'giss-vosk-2';
const murl = (l) => `${API}/${l}.tar.gz?v=2`;

export async function hasOfflineModel(l = appLang()) {
  try { return !!(await (await caches.open(CACHE)).match(murl(l))); } catch { return false; }
}

async function loadModel(l, onLoad) {
  if (models[l]) return models[l];
  const url = murl(l), c = await caches.open(CACHE);
  let res = await c.match(url);
  if (!res) {
    if (!navigator.onLine) throw new Error('noModel');
    onLoad?.();
    res = await fetch(url);
    if (!res.ok) throw new Error('noModel');
    await c.put(url, res.clone());
  }
  const { createModel } = await import('vosk-browser');
  const to = new Promise((_, rej) => setTimeout(() => rej(new Error('noModel')), 90000));
  models[l] = await Promise.race([createModel(URL.createObjectURL(await res.blob())), to]);
  return models[l];
}

async function voskListen(l, onText, onLoad, grammar) {
  const model = await loadModel(l, onLoad);
  const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 } });
  const ac = new (window.AudioContext || window.webkitAudioContext)();
  const rec = grammar ? new model.KaldiRecognizer(ac.sampleRate, JSON.stringify([...grammar(l), '[unk]'])) : new model.KaldiRecognizer(ac.sampleRate);
  rec.on('result', (m) => { const s = m.result?.text; if (s) onText(s); });
  const node = ac.createScriptProcessor(4096, 1, 1), src = ac.createMediaStreamSource(stream);
  node.onaudioprocess = (e) => { try { rec.acceptWaveform(e.inputBuffer); } catch { /* model busy */ } };
  src.connect(node); node.connect(ac.destination);
  return () => { try { node.disconnect(); src.disconnect(); stream.getTracks().forEach((x) => x.stop()); rec.remove(); ac.close(); } catch { /* closed */ } };
}

function deviceListen(l, onText, onError) {
  const r = new SR();
  if (kritisOn() || !navigator.onLine) {
    if (!('processLocally' in r)) throw new Error(kritisOn() ? 'kritis' : 'noSr');
    r.processLocally = true;
  }
  let want = true;
  r.lang = LOC[l] || 'de-DE'; r.continuous = true; r.interimResults = false;
  r.onresult = (e) => { for (let i = e.resultIndex; i < e.results.length; i++) if (e.results[i].isFinal) onText(e.results[i][0].transcript); };
  r.onerror = (e) => { if (['not-allowed', 'service-not-allowed', 'language-not-supported'].includes(e.error)) { want = false; onError('noSr'); } };
  r.onend = () => { if (want) try { r.start(); } catch { onError('noSr'); } };
  r.start();
  return () => { want = false; try { r.stop(); } catch { /* ended */ } };
}

// returns { stop, engine: 'offline' | 'device' }; errors: noSr | kritis | noModel
export async function listen({ onText, onError, onLoad, grammar }) {
  const l = appLang(), off = OFFLINE_LANGS.includes(l);
  const vosk = off && (kritisOn() || !navigator.onLine || !SR || (await hasOfflineModel(l)));
  if (vosk) return { stop: await voskListen(l, onText, onLoad, grammar), engine: 'offline' };
  if (!SR) throw new Error(kritisOn() ? 'kritis' : 'noSr');
  return { stop: deviceListen(l, onText, onError), engine: 'device' };
}

// explicit download of the offline package while online
export const fetchOfflineModel = (onLoad) => loadModel(appLang(), onLoad);

// vocabulary for number dictation (restricts the offline model -> far fewer misrecognitions)
const U = { de: ['null', 'eins', 'ein', 'zwei', 'drei', 'vier', 'fünf', 'sechs', 'sieben', 'acht', 'neun'], en: ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'] };
export function numberGrammar(l) {
  if (l === 'en') {
    const tens = ['twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
    return [...U.en, 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', ...tens, ...tens.flatMap((x) => U.en.slice(1).map((u) => `${x} ${u}`)), 'hundred', 'point', 'row', 'field', 'column', 'is', 'wrong', 'undo'];
  }
  const tens = ['zwanzig', 'dreißig', 'vierzig', 'fünfzig', 'sechzig', 'siebzig', 'achtzig', 'neunzig'];
  const u = ['ein', 'zwei', 'drei', 'vier', 'fünf', 'sechs', 'sieben', 'acht', 'neun'];
  return [...U.de, 'zehn', 'elf', 'zwölf', 'dreizehn', 'vierzehn', 'fünfzehn', 'sechzehn', 'siebzehn', 'achtzehn', 'neunzehn', ...tens, ...tens.flatMap((x) => u.map((y) => `${y}und${x}`)), 'hundert', 'komma', 'zeile', 'reihe', 'feld', 'spalte', 'ist', 'falsch'];
}
